'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { z } from 'zod';

import type { CommandPullRequest } from './types';

import { safeLocalStorage } from '@/lib/safe-storage';
import { STORAGE_KEYS } from '@/lib/storage-keys';

const MAX_RECENT_PULL_REQUESTS = 20;

const NO_PULL_REQUESTS: readonly CommandPullRequest[] = [];

const commandPullRequestSchema = z.object({
  owner: z.string(),
  repo: z.string(),
  number: z.number(),
  title: z.string(),
  state: z.enum(['open', 'draft', 'merged', 'closed']),
  reviewState: z.enum(['approved', 'changes-requested', 'review-required']).nullable(),
  authorLogin: z.string().nullable(),
  authorAvatarUrl: z.string().nullable(),
  updatedAt: z.string().nullable(),
  needsReview: z.boolean(),
}) satisfies z.ZodType<CommandPullRequest>;

/** The command id of a pull request row, the same whichever source lists the pull request. */
export function getPullRequestCommandId(pr: {
  owner: string;
  repo: string;
  number: number;
}): string {
  return `pull-request:${pr.owner}/${pr.repo}#${pr.number}`.toLowerCase();
}

// Entries another version of the app wrote, or that were edited by hand, are dropped one by one.
function parseRecentPullRequests(raw: string | null): readonly CommandPullRequest[] {
  if (!raw) {
    return NO_PULL_REQUESTS;
  }

  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return NO_PULL_REQUESTS;
  }
  if (!Array.isArray(value)) {
    return NO_PULL_REQUESTS;
  }

  return value.flatMap((entry) => {
    const parsed = commandPullRequestSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
}

const parsedByKey = new Map<
  string,
  { raw: string | null; pullRequests: readonly CommandPullRequest[] }
>();

// Cached on the stored string, so the list keeps its identity until the storage changes.
function getRecentPullRequests(userId: string) {
  const key = STORAGE_KEYS.RECENT_PULL_REQUESTS(userId);
  const raw = safeLocalStorage.getItem(key);
  const cached = parsedByKey.get(key);
  if (cached?.raw === raw) {
    return cached.pullRequests;
  }

  const pullRequests = parseRecentPullRequests(raw);
  parsedByKey.set(key, { raw, pullRequests });
  return pullRequests;
}

const getNoPullRequests = () => NO_PULL_REQUESTS;

const listeners = new Set<() => void>();

function subscribe(listener: () => void) {
  listeners.add(listener);
  // Another tab may record one.
  window.addEventListener('storage', listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', listener);
  };
}

/** Puts the pull request first in the user's list, keeping each pull request once and the newest 20. */
export function recordRecentPullRequest(userId: string, pullRequest: CommandPullRequest): void {
  const id = getPullRequestCommandId(pullRequest);
  const next = [
    pullRequest,
    ...getRecentPullRequests(userId).filter((entry) => getPullRequestCommandId(entry) !== id),
  ].slice(0, MAX_RECENT_PULL_REQUESTS);

  safeLocalStorage.setItem(STORAGE_KEYS.RECENT_PULL_REQUESTS(userId), JSON.stringify(next));
  for (const listener of listeners) {
    listener();
  }
}

/** The pull requests this user opened most recently in this browser, newest first. */
export function useRecentPullRequests(
  userId: string | undefined,
  limit = 5,
): readonly CommandPullRequest[] {
  const getSnapshot = useCallback(
    () => (userId ? getRecentPullRequests(userId) : NO_PULL_REQUESTS),
    [userId],
  );
  const recentPullRequests = useSyncExternalStore(subscribe, getSnapshot, getNoPullRequests);
  return useMemo(() => recentPullRequests.slice(0, limit), [limit, recentPullRequests]);
}
